
import React from 'react';

interface PingOverlayProps {
  active: boolean;
  beat: number;
  status?: string;
  latency?: number | null;
  error?: string | null;
  hardwareType?: 'WIRED' | 'WIRELESS' | 'UNKNOWN';
  onForceApply?: () => void;
}

export const PingOverlay: React.FC<PingOverlayProps> = ({ active, beat, status, latency, error, hardwareType, onForceApply }) => {
  const isVisible = active || status === 'CALIBRATED' || status === 'ERROR';
  if (!isVisible) return null;

  const isCalibrated = status === 'CALIBRATED';
  const isFault = error === 'HARDWARE_FAULT';
  const isWireless = hardwareType === 'WIRELESS' || (latency !== null && latency !== undefined && latency > 160);

  return (
    <div className="absolute inset-0 z-[5000] flex items-center justify-center pointer-events-auto bg-black/95 backdrop-blur-3xl animate-fade-in">
      <div className="flex flex-col items-center gap-12 text-center px-10">
          
          {isFault ? (
              <div className="flex flex-col items-center gap-8 animate-success-reveal max-w-xl">
                  <div className="w-24 h-24 rounded-full bg-red-600/20 border-2 border-red-600 flex items-center justify-center text-4xl shadow-[0_0_40px_rgba(220,38,38,0.4)]">🚨</div>
                  <div className="space-y-4">
                      <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter">Désynchronisation <span className="text-red-500">Système</span></h2>
                      <p className="text-sm text-slate-300 font-medium italic">Signal instable détecté.</p>
                  </div>
                  <button onClick={() => window.location.reload()} className="w-full py-5 bg-white text-black rounded-full font-black uppercase text-[10px] tracking-widest shadow-2xl">REESSAYER</button>
              </div>
          ) : !isCalibrated ? (
              <>
                <div className="relative flex items-center justify-center">
                    <div className="w-48 h-48 border-[4px] border-cyan-400/20 rounded-full flex items-center justify-center">
                        <div className="w-40 h-40 border-[8px] border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <div className="absolute flex flex-col items-center">
                        <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest mb-1">Ping Test</span>
                        <div className="text-6xl font-black text-white italic">{beat || 1}</div>
                    </div>
                </div>
                <h2 className="text-4xl font-black text-white italic tracking-tighter uppercase">Calibration <br/><span className="text-cyan-400">Micro-System</span></h2>
                <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Ne jouez pas pendant ce test.</p>
              </>
          ) : (
              <div className="flex flex-col items-center gap-8 animate-success-reveal scale-110">
                  <div className="w-32 h-32 rounded-full bg-emerald-500 flex items-center justify-center shadow-[0_0_50px_rgba(16,185,129,0.4)] border-4 border-white animate-bounce-slow">
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                  <div className="space-y-6 text-center">
                      <div>
                          <h2 className="text-6xl font-black text-white italic tracking-tighter uppercase">
                              {(latency!).toFixed(1)} <span className="text-xl">ms</span>
                          </h2>
                          <div className={`text-[10px] font-black uppercase tracking-widest mt-2 px-4 py-1 rounded-full inline-block ${isWireless ? 'bg-orange-500/20 text-orange-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                            {isWireless ? '🔗 Détection Bluetooth / Sans-fil' : '🔌 Détection Filaire / Interne'}
                          </div>
                      </div>
                  </div>
              </div>
          )}
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        @keyframes success-reveal { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-success-reveal { animation: success-reveal 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};
